import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  PlatformSocialLink,
  PlatformSocialLinksResponse,
  PlatformSocialLinksService,
  SocialNetworkOption,
} from '../../services/platform-social-links.service';
import { NotificationService } from '../../services/notification.service';
import { DialogService } from '../../services/dialog.service';
import { friendlyError } from '../../utils/friendly-error';
import { SocialNetworkSelectComponent } from './social-network-select.component';
import { KuboAvatarComponent } from './kubo-avatar.component';
import { BusinessSocialLink } from '../../models/sala.model';

interface SocialRow extends PlatformSocialLink {
  uid: number;
}

interface DraftLink {
  network: string;
  label: string;
  url: string;
  is_active: boolean;
}

const FALLBACK_NETWORK: SocialNetworkOption = {
  key: 'other', label: 'Otra red', icon: 'bi-link-45deg', color: '#6366F1', on_color: '#ffffff', placeholder: 'https://...',
};

// Misma regla que normalizeSocialUrl en el backend (helpers/platformSocialLinks.ts):
// acepta atajos (número de WhatsApp, @usuario de Telegram, dominio sin https://)
// y solo admite http/https. El backend vuelve a validar al guardar.
export const normalizeSocialUrl = (network: string, raw: string): string | null => {
  let value = String(raw ?? '').trim();
  if (!value) return '';
  if (network === 'whatsapp' && /^\+?[\d\s().-]{7,24}$/.test(value)) {
    const digits = value.replace(/\D/g, '');
    if (digits.length < 7 || digits.length > 15) return null;
    value = `https://wa.me/${digits}`;
  } else if (network === 'telegram' && /^@[A-Za-z0-9_]{3,64}$/.test(value)) {
    value = `https://t.me/${value.slice(1)}`;
  } else if (!/^[a-z][a-z0-9+.-]*:/i.test(value)) {
    value = `https://${value.replace(/^\/+/, '')}`;
  }
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
    if (!parsed.hostname.includes('.') || parsed.username || parsed.password) return null;
    const normalized = parsed.toString();
    return normalized.length <= 500 ? normalized : null;
  } catch {
    return null;
  }
};

/**
 * Valida y normaliza una lista de redes con las mismas reglas que el backend
 * (enlace válido, obligatorio si está visible, sin enlaces repetidos). Los
 * errores se indexan por posición en la lista. La comparten el editor y
 * "Mi sitio" (que guarda las redes junto con el resto del borrador).
 */
export const validateSocialLinks = (
  links: Array<Pick<PlatformSocialLink, 'network' | 'label' | 'url' | 'is_active'> & { id?: number | null }>,
  network: (key: string) => SocialNetworkOption,
): { ok: boolean; errors: Record<number, string>; links: PlatformSocialLink[] } => {
  const errors: Record<number, string> = {};
  const seen = new Map<string, string>();
  const clean = links.map((row, index) => {
    const net = network(row.network);
    const label = String(row.label || '').trim().replace(/\s+/g, ' ') || net.label;
    const url = normalizeSocialUrl(row.network, row.url);
    if (url === null) {
      errors[index] = `El enlace de ${label} no es válido. Ejemplo: ${net.placeholder}`;
    } else if (row.is_active && !url) {
      errors[index] = `Agrega el enlace de ${label} o márcala como oculta.`;
    } else if (url) {
      const key = url.toLowerCase().replace(/\/+$/, '');
      if (seen.has(key)) errors[index] = `Este enlace ya está registrado en ${seen.get(key)}.`;
      else seen.set(key, label);
    }
    return { id: row.id ?? null, network: row.network, label, url: url || '', is_active: row.is_active };
  });
  return { ok: Object.keys(errors).length === 0, errors, links: clean };
};

@Component({
  selector: 'app-platform-social-links-admin',
  standalone: true,
  imports: [CommonModule, FormsModule, SocialNetworkSelectComponent, KuboAvatarComponent],
  template: `
    <!-- .social-shell es el contenedor de las container queries: el diseño se
         adapta al ancho real disponible (panel de plataforma o columna de
         "Mi sitio"), no al de la ventana. El modal queda FUERA a propósito:
         un contenedor de consultas sería el bloque contenedor de su position:fixed. -->
    <div class="social-shell" *ngIf="!loading(); else loadingState">
    <div class="social-layout">
      <!-- Columna principal: listado ordenable -->
      <section class="social-card" aria-label="Redes sociales configuradas">
        <!-- Barra de herramientas (el título ya lo da la pestaña "Redes sociales"). -->
        <header class="card-head">
          <p class="card-hint">
            <span class="count-pill" [class.full]="atLimit()" [attr.aria-label]="rows().length + ' de ' + limit() + ' redes'">{{ rows().length }}<small>/{{ limit() }}</small></span>
            <span>Arrastra <i class="bi bi-grip-vertical" aria-hidden="true"></i> para ordenar. Solo se publican las <strong>visibles</strong>.</span>
          </p>
          <button type="button" class="primary-add" (click)="openCreate()" [disabled]="atLimit()"><i class="bi bi-plus-lg"></i><span>Agregar red</span></button>
        </header>

        <div class="quick-add" *ngIf="missingSuggested().length && !atLimit()">
          <span class="quick-label">Sugeridas</span>
          <button type="button" class="quick-btn" *ngFor="let net of missingSuggested()" (click)="openCreate(net.key)"
                  [style.--brand]="net.color" [style.--on-brand]="net.on_color" [attr.aria-label]="'Agregar ' + net.label">
            <i class="bi" [ngClass]="net.icon" aria-hidden="true"></i><span>{{ net.label }}</span>
          </button>
        </div>

        <ol class="social-list" *ngIf="rows().length; else emptyList" [class.is-dragging]="dragUid() !== null">
          <li class="social-row" *ngFor="let row of rows(); let i = index; trackBy: trackRow"
              [attr.data-uid]="row.uid" [class.is-hidden]="!row.is_active" [class.invalid]="!!errors()[row.uid]"
              [class.dragging]="dragUid() === row.uid" [style.--brand]="network(row.network).color" [style.--on-brand]="network(row.network).on_color">
            <button type="button" class="drag-handle" (pointerdown)="startDrag($event, row.uid)" (keydown)="handleKey($event, i)"
                    [attr.aria-label]="'Mover ' + (row.label || 'red') + ', posición ' + (i + 1) + ' de ' + rows().length + '. Usa las flechas arriba y abajo.'" title="Arrastra para ordenar">
              <i class="bi bi-grip-vertical" aria-hidden="true"></i>
            </button>

            <div class="row-fields">
              <div class="field field-net">
                <span [id]="'social-net-label-' + row.uid">Red</span>
                <app-social-network-select [options]="catalog()" [value]="row.network" [used]="usedNetworks()" ariaLabel="Red social"
                                           (valueChange)="changeNetwork(i, $event)"></app-social-network-select>
              </div>
              <label class="field field-label">
                <span>Nombre visible</span>
                <input type="text" maxlength="40" [ngModel]="row.label" (ngModelChange)="patch(i, { label: $event })" [placeholder]="network(row.network).label" data-no-limit-hint />
              </label>
              <label class="field field-url">
                <span>Enlace <em *ngIf="row.is_active" class="req">*</em></span>
                <input type="url" inputmode="url" maxlength="500" [id]="'social-url-' + row.uid" [ngModel]="row.url" (ngModelChange)="patch(i, { url: $event })"
                       (blur)="normalizeRow(i)" [placeholder]="network(row.network).placeholder" [class.invalid]="!!errors()[row.uid]" autocomplete="off" spellcheck="false" data-no-limit-hint />
              </label>
            </div>

            <div class="row-actions">
              <button type="button" role="switch" class="visibility-switch" [class.on]="row.is_active" [attr.aria-checked]="row.is_active"
                      (click)="patch(i, { is_active: !row.is_active })" [title]="row.is_active ? (isBusiness ? 'Visible en tu sitio: clic para ocultar' : 'Visible en los frentes: clic para ocultar') : (isBusiness ? 'Oculta: clic para mostrarla en tu sitio' : 'Oculta: clic para mostrarla en los frentes')">
                <span class="switch-track"><span class="switch-knob"></span></span>
                <span class="switch-label">{{ row.is_active ? 'Visible' : 'Oculta' }}</span>
              </button>
              <a class="icon-action" *ngIf="openableUrl(row) as href; else noLink" [href]="href" target="_blank" rel="noopener noreferrer" title="Abrir enlace en una pestaña nueva" aria-label="Abrir enlace"><i class="bi bi-box-arrow-up-right"></i></a>
              <ng-template #noLink><span class="icon-action is-disabled" title="Agrega un enlace válido para probarlo" aria-hidden="true"><i class="bi bi-box-arrow-up-right"></i></span></ng-template>
              <button type="button" class="icon-action danger" (click)="remove(i)" title="Quitar red" [attr.aria-label]="'Quitar ' + (row.label || 'red')"><i class="bi bi-trash3"></i></button>
            </div>

            <p class="row-error" *ngIf="errors()[row.uid] as message" role="alert"><i class="bi bi-exclamation-circle"></i> {{ message }}</p>
          </li>
        </ol>
        <ng-template #emptyList>
          <div class="social-empty">
            <span class="empty-icon"><i class="bi bi-share"></i></span>
            <strong>Aún no hay redes sociales</strong>
            <span>{{ isBusiness ? 'Agrega las redes de tu negocio para mostrarlas en la sección de contacto de tu sitio.' : 'Agrega las redes oficiales de TuSala para mostrarlas en el sitio de clientes, Kubo y la landing.' }}</span>
            <button type="button" class="primary-add" (click)="openCreate()"><i class="bi bi-plus-lg"></i><span>Agregar primera red</span></button>
          </div>
        </ng-template>

        <footer class="social-actions" [class.dirty]="dirty()">
          <span class="save-state" [class.pending]="dirty()">
            <i class="bi" [class.bi-check2-circle]="!dirty()" [class.bi-circle-fill]="dirty()" aria-hidden="true"></i>
            {{ dirty() ? 'Tienes cambios sin guardar' : (isBusiness ? 'Borrador al día' : 'Todo está guardado') }}
          </span>
          <div class="action-buttons">
            <button type="button" class="secondary-btn" (click)="discard()" [disabled]="isSaving() || !dirty()">Descartar</button>
            <button type="button" class="action-btn" (click)="save()" [disabled]="isSaving() || !dirty()">
              <i class="bi" [class.bi-cloud-check]="!isSaving()" [class.bi-arrow-repeat]="isSaving()" [class.spin]="isSaving()"></i>{{ isSaving() ? 'Guardando...' : (isBusiness ? 'Guardar borrador' : 'Guardar cambios') }}
            </button>
          </div>
        </footer>
        <span class="sr-only" aria-live="polite">{{ liveMessage() }}</span>
      </section>

      <!-- Columna lateral: cómo lo verá el público -->
      <aside class="preview-card" aria-label="Vista previa pública">
        <header>
          <span class="eyebrow">VISTA PREVIA</span>
          <span class="preview-badge" [class.pending]="dirty()">{{ dirty() ? 'Sin guardar' : (isBusiness ? 'Guardado' : 'Publicado') }}</span>
        </header>

        <ng-container *ngIf="isBusiness; else platformPreview">
          <div class="preview-block">
            <small><i class="bi bi-headset"></i> Sección «Contacto» de tu sitio</small>
            <div class="mock-site">
              <span class="mock-site-kicker"><i class="bi bi-headset" aria-hidden="true"></i> Contacto</span>
              <strong class="mock-site-title">Hablemos</strong>
              <div class="mock-site-lines" aria-hidden="true"><span></span><span></span></div>
              <div class="mock-icons" *ngIf="previewLinks().length; else noPreview">
                <a *ngFor="let link of previewLinks(); trackBy: trackRow" class="mock-icon site" [href]="link.url" target="_blank" rel="noopener noreferrer"
                   [title]="link.label" [attr.aria-label]="link.label" [style.--brand]="network(link.network).color" [style.--on-brand]="network(link.network).on_color">
                  <i class="bi" [ngClass]="network(link.network).icon" aria-hidden="true"></i>
                </a>
              </div>
            </div>
          </div>

          <div class="preview-block">
            <small><i class="bi bi-phone"></i> En el celular de tus clientes</small>
            <div class="mock-phone">
              <span class="mock-phone-notch" aria-hidden="true"></span>
              <div class="mock-icons centered" *ngIf="previewLinks().length; else noPreview">
                <span *ngFor="let link of previewLinks(); trackBy: trackRow" class="mock-icon filled" [title]="link.label"
                      [style.--brand]="network(link.network).color" [style.--on-brand]="network(link.network).on_color">
                  <i class="bi" [ngClass]="network(link.network).icon" aria-hidden="true"></i>
                </span>
              </div>
            </div>
          </div>
        </ng-container>

        <ng-template #platformPreview>
        <div class="preview-block">
          <small><i class="bi bi-layout-text-window-reverse"></i> Footer del sitio y la landing</small>
          <div class="mock-footer">
            <span class="mock-footer-title">Síguenos</span>
            <div class="mock-icons" *ngIf="previewLinks().length; else noPreview">
              <a *ngFor="let link of previewLinks(); trackBy: trackRow" class="mock-icon" [href]="link.url" target="_blank" rel="noopener noreferrer"
                 [title]="link.label" [attr.aria-label]="link.label" [style.--brand]="network(link.network).color" [style.--on-brand]="network(link.network).on_color">
                <i class="bi" [ngClass]="network(link.network).icon" aria-hidden="true"></i>
              </a>
            </div>
          </div>
        </div>

        <div class="preview-block">
          <small class="mock-kubo-label"><app-kubo-avatar class="mock-kubo-avatar" mood="saludo"></app-kubo-avatar> Kubo y modal de contacto</small>
          <div class="mock-kubo">
            <div class="mock-kubo-option"><span><i class="bi bi-share-fill"></i></span><div><strong>Síguenos en redes</strong><em>Redes oficiales de TuSala</em></div><i class="bi bi-chevron-up"></i></div>
            <div class="mock-icons centered" *ngIf="previewLinks().length; else noPreview">
              <span *ngFor="let link of previewLinks(); trackBy: trackRow" class="mock-icon filled" [title]="link.label"
                    [style.--brand]="network(link.network).color" [style.--on-brand]="network(link.network).on_color">
                <i class="bi" [ngClass]="network(link.network).icon" aria-hidden="true"></i>
              </span>
            </div>
          </div>
        </div>

        </ng-template>

        <ng-template #noPreview><span class="preview-empty"><i class="bi bi-eye-slash"></i> {{ isBusiness ? 'Sin redes visibles: no se mostrarán íconos.' : 'Sin redes visibles: la sección se oculta.' }}</span></ng-template>

        <ul class="preview-tips">
          <li><i class="bi bi-eye"></i><span>Solo se publican las redes <strong>visibles</strong> con enlace.</span></li>
          <li><i class="bi bi-arrows-move"></i><span>{{ isBusiness ? 'El orden de la lista es el orden en tu sitio.' : 'El orden de la lista es el orden público.' }}</span></li>
          <li *ngIf="!isBusiness"><i class="bi bi-clock-history"></i><span>Los cambios se reflejan en hasta 1 minuto.</span></li>
          <li *ngIf="isBusiness"><i class="bi bi-cloud-arrow-up"></i><span>Guarda el borrador y usa <strong>Publicar cambios</strong> para que tus clientes las vean.</span></li>
        </ul>
      </aside>
    </div>
    </div>

    <!-- Modal de creación: un solo paso compacto con selector con buscador. -->
    <div *ngIf="createOpen()" class="modal-overlay social-modal-overlay" (click)="closeCreate()">
      <section class="modal-box social-modal" (click)="$event.stopPropagation()" role="dialog" aria-modal="true" aria-labelledby="social-modal-title">
        <header class="sm-header" [style.--brand]="draftNetwork()?.color || null" [style.--on-brand]="draftNetwork()?.on_color || null">
          <span class="sm-badge" [class.branded]="!!draftNetwork()" aria-hidden="true"><i class="bi" [ngClass]="draftNetwork()?.icon || 'bi-plus-lg'"></i></span>
          <div class="sm-heading">
            <h3 id="social-modal-title">Nueva red social</h3>
            <p>Se agrega al final de la lista; podrás reordenarla arrastrándola.</p>
          </div>
          <button type="button" class="sm-icon-btn" aria-label="Cerrar" (click)="closeCreate()"><i class="bi bi-x-lg"></i></button>
        </header>
          <form class="sm-body sm-form" (ngSubmit)="confirmCreate()" novalidate>
            <div class="field">
              <span>Red social <em class="req">*</em></span>
              <app-social-network-select #draftSelect [options]="catalog()" [value]="draft.network" [used]="usedNetworks()" [invalid]="!!networkError()"
                                         placeholder="Busca y elige una red" ariaLabel="Red social" (valueChange)="pickNetwork($event)"></app-social-network-select>
              <small class="field-error" *ngIf="networkError()" role="alert"><i class="bi bi-exclamation-circle"></i> {{ networkError() }}</small>
            </div>
            <label class="field">
              <span>Nombre visible</span>
              <input type="text" name="draftLabel" maxlength="40" [(ngModel)]="draft.label" [placeholder]="draftNetwork()?.label || (isBusiness ? 'Ej. Instagram de mi negocio' : 'Ej. Instagram de TuSala')" data-no-limit-hint />
              <small class="field-hint">Se usa como texto accesible y al pasar el cursor sobre el ícono.</small>
            </label>
            <label class="field">
              <span>Enlace <em class="req">*</em></span>
              <input type="url" id="social-draft-url" name="draftUrl" inputmode="url" maxlength="500" [(ngModel)]="draft.url" (ngModelChange)="draftError.set('')"
                     [placeholder]="network(draft.network).placeholder" [class.invalid]="!!draftError()" autocomplete="off" spellcheck="false" data-no-limit-hint />
              <small class="field-hint" *ngIf="!draftError()">{{ draftHint() }}</small>
              <small class="field-error" *ngIf="draftError()" role="alert"><i class="bi bi-exclamation-circle"></i> {{ draftError() }}</small>
            </label>
            <button type="button" role="switch" class="visibility-switch wide" [class.on]="draft.is_active" [attr.aria-checked]="draft.is_active" (click)="draft.is_active = !draft.is_active">
              <span class="switch-track"><span class="switch-knob"></span></span>
              <span class="switch-text"><strong>{{ draft.is_active ? (isBusiness ? 'Visible en tu sitio' : 'Visible en los frentes') : 'Guardar como oculta' }}</strong><em>{{ draft.is_active ? 'Se mostrará al guardar los cambios.' : 'Quedará en la lista sin publicarse.' }}</em></span>
            </button>
            <footer class="sm-footer">
              <button type="button" class="secondary-btn" (click)="closeCreate()">Cancelar</button>
              <button type="submit" class="action-btn"><i class="bi bi-plus-lg"></i> Agregar a la lista</button>
            </footer>
          </form>
      </section>
    </div>

    <ng-template #loadingState>
      <div class="social-loading" *ngIf="!loadError(); else loadErrorState"><span class="spinner"></span><span>Cargando redes sociales…</span></div>
      <ng-template #loadErrorState>
        <div class="social-load-error" role="alert">
          <i class="bi bi-wifi-off"></i><span>{{ loadError() }}</span>
          <button type="button" class="secondary-btn" (click)="load()"><i class="bi bi-arrow-clockwise"></i> Reintentar</button>
        </div>
      </ng-template>
    </ng-template>
  `,
  styles: [`
    :host{display:block}
    .sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
    .eyebrow{color:var(--ts-text-subtle);font-size:.6rem;font-weight:800;letter-spacing:.1em}

    /* ---------- Layout ---------- */
    .social-layout{display:grid;grid-template-columns:minmax(0,1fr) 300px;gap:1.1rem;align-items:start}
    .social-card,.preview-card{border:1px solid var(--ts-border-strong);border-radius:16px;background:linear-gradient(165deg,var(--ts-surface),var(--ts-bg-elevated));box-shadow:var(--ts-shadow-sm)}
    .social-card{position:relative;display:grid;gap:1rem;padding:1.15rem 1.2rem 0}

    /* ---------- Cabecera del listado ---------- */
    .card-head{display:flex;align-items:center;justify-content:space-between;gap:.8rem 1rem;flex-wrap:wrap}
    .card-hint{display:flex;align-items:center;gap:.65rem;min-width:0;margin:0;color:var(--ts-text-muted);font-size:.78rem;line-height:1.4}
    .card-hint i{color:var(--ts-text);font-size:.82rem}
    .card-hint strong{color:var(--ts-text)}
    .count-pill{display:inline-flex;align-items:baseline;gap:1px;padding:.3rem .65rem;color:var(--ts-text);border:1px solid var(--ts-border-strong);border-radius:999px;font-size:.8rem;font-weight:800}
    .count-pill small{color:var(--ts-text-subtle);font-size:.68rem}
    .count-pill.full{color:var(--ts-danger);border-color:color-mix(in srgb,var(--ts-danger) 45%,transparent)}
    .primary-add{display:inline-flex;align-items:center;justify-content:center;gap:.45rem;height:40px;padding:0 1rem;color:var(--ts-text-on-accent);border:0;border-radius:11px;background:linear-gradient(135deg,var(--ts-primary),var(--ts-indigo,#6366f1));box-shadow:0 8px 20px color-mix(in srgb,var(--ts-primary) 30%,transparent);font-size:.8rem;font-weight:800;cursor:pointer;transition:transform var(--ts-transition),box-shadow var(--ts-transition),filter var(--ts-transition)}
    .primary-add:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 12px 26px color-mix(in srgb,var(--ts-primary) 38%,transparent)}
    .primary-add:active:not(:disabled){transform:scale(.97)}
    .primary-add:disabled{opacity:.45;cursor:not-allowed;filter:saturate(.5)}

    .quick-add{display:flex;flex-wrap:wrap;align-items:center;gap:.45rem;padding:.6rem .7rem;border:1px dashed var(--ts-border-strong);border-radius:12px}
    .quick-label{margin-right:.25rem;color:var(--ts-text-subtle);font-size:.62rem;font-weight:800;letter-spacing:.08em;text-transform:uppercase}
    .quick-btn{display:inline-flex;align-items:center;gap:.4rem;min-height:32px!important;height:32px;padding:0 .75rem 0 .35rem;color:var(--ts-text-muted);border:1px solid var(--ts-border-strong);border-radius:999px;background:var(--ts-bg-elevated);font-size:.74rem;cursor:pointer;transition:color var(--ts-transition),border-color var(--ts-transition),transform var(--ts-transition)}
    .quick-btn i{display:grid;place-items:center;box-sizing:border-box;width:24px;height:24px;min-width:24px;min-height:24px;flex:0 0 24px;aspect-ratio:1/1;padding:0;color:var(--on-brand);border-radius:50%;background:var(--brand);font-size:.75rem;line-height:1}
    .quick-btn:hover{color:var(--ts-text);border-color:var(--brand);transform:translateY(-1px)}

    /* ---------- Filas ---------- */
    /* La fila se adapta al ANCHO DEL LISTADO (container query), no de la
       ventana: con el menú lateral abierto la tarjeta es mucho más angosta. */
    .social-list{display:grid;gap:.6rem;margin:0;padding:0;list-style:none;container-type:inline-size;container-name:social-list}
    .social-row{position:relative;display:grid;grid-template-columns:28px minmax(150px,.95fr) minmax(110px,.85fr) minmax(180px,1.7fr) auto;grid-template-areas:"handle net label url actions";align-items:end;gap:.55rem .7rem;padding:.7rem .85rem .75rem .55rem;border:1px solid var(--ts-border);border-radius:14px;background:var(--ts-bg-elevated);animation:row-in .26s cubic-bezier(.2,.8,.2,1) both;transition:border-color var(--ts-transition),box-shadow var(--ts-transition),background var(--ts-transition)}
    .social-row::before{content:'';position:absolute;left:-1px;top:14px;bottom:14px;width:3px;border-radius:0 3px 3px 0;background:var(--brand);opacity:.85}
    .social-row:hover{border-color:var(--ts-border-strong)}
    .social-row.is-hidden::before{opacity:.25}
    .social-row.is-hidden :is(.field-net,.field-label,.field-url){opacity:.62}
    .social-row.invalid{border-color:var(--ts-danger);box-shadow:0 0 0 3px color-mix(in srgb,var(--ts-danger) 16%,transparent)}
    .social-row.dragging{z-index:5;border-color:var(--ts-primary);background:var(--ts-surface);box-shadow:0 18px 40px var(--ts-shadow-gray-1000-a40),0 0 0 2px color-mix(in srgb,var(--ts-primary) 40%,transparent);transition:box-shadow var(--ts-transition),background var(--ts-transition);animation:none}
    .social-list.is-dragging,.social-list.is-dragging *{cursor:grabbing!important;user-select:none}
    .drag-handle{display:grid;place-items:center;width:28px;min-height:44px!important;height:44px;padding:0;color:var(--ts-text-subtle);border:0;border-radius:8px;background:transparent;font-size:1.15rem;cursor:grab;touch-action:none;transition:color var(--ts-transition),background var(--ts-transition)}
    .drag-handle:hover,.social-row.dragging .drag-handle{color:var(--ts-text);background:var(--ts-surface-hover)}
    .drag-handle:focus-visible{outline:3px solid color-mix(in srgb,var(--ts-primary) 45%,transparent);outline-offset:1px}
    .row-fields{display:contents}
    .drag-handle{grid-area:handle;align-self:center}
    .field-net{grid-area:net}
    .field-label{grid-area:label}
    .field-url{grid-area:url}
    .row-actions{grid-area:actions;align-self:end;min-height:38px}
    @container social-list (max-width: 860px){
      .social-row{grid-template-columns:28px minmax(0,1fr) minmax(0,1fr) auto;grid-template-areas:"handle net label actions" "handle url url url"}
    }
    @container social-list (max-width: 540px){
      .social-row{grid-template-columns:28px minmax(0,1fr) auto;grid-template-areas:"handle net actions" "handle label label" "handle url url";padding:.7rem .7rem .8rem .4rem}
    }
    /* Celular: el selector de red necesita todo el ancho -- las acciones
       (visible, abrir, quitar) bajan a su propia línea al final de la fila. */
    @container social-list (max-width: 420px){
      .social-row{grid-template-columns:28px minmax(0,1fr);grid-template-areas:"handle net" "handle label" "handle url" "handle actions"}
      .row-actions{justify-content:flex-end;padding-top:.15rem}
      .visibility-switch:not(.wide){margin-right:auto}
    }
    @container social-list (max-width: 280px){
      .visibility-switch:not(.wide){min-width:0;padding:0 .4rem}
      .visibility-switch:not(.wide) .switch-label{display:none}
    }
    .field{display:grid;gap:.28rem;min-width:0;margin:0}
    .field>span{color:var(--ts-text-subtle);font-size:.64rem!important;font-weight:800!important;letter-spacing:.04em;text-transform:uppercase}
    .field .req{color:var(--ts-danger);font-style:normal}
    .field :is(input,select){box-sizing:border-box;width:100%;height:38px;min-height:38px!important;padding:0 .7rem!important;color:var(--ts-text);border:1px solid var(--ts-border-strong);border-radius:10px;background:var(--ts-surface);font-size:.82rem!important;outline:none;transition:border-color var(--ts-transition),box-shadow var(--ts-transition)}
    .field :is(input,select):focus{border-color:var(--ts-primary);box-shadow:0 0 0 3px color-mix(in srgb,var(--ts-primary) 22%,transparent)}
    .field input.invalid{border-color:var(--ts-danger);box-shadow:0 0 0 3px color-mix(in srgb,var(--ts-danger) 18%,transparent)}
    .field input::placeholder{color:var(--ts-text-subtle)}
    .field-hint{color:var(--ts-text-subtle);font-size:.7rem;line-height:1.4}
    .field-error{display:flex;align-items:center;gap:.35rem;color:var(--ts-danger);font-size:.72rem;font-weight:700}
    .row-actions{display:flex;align-items:center;gap:.4rem}
    .row-error{grid-column:2/-1;display:flex;align-items:center;gap:.4rem;margin:-.15rem 0 0;color:var(--ts-danger);font-size:.74rem;font-weight:650}

    .visibility-switch{display:inline-flex;align-items:center;gap:.45rem;min-width:96px;min-height:34px!important;height:34px;padding:0 .65rem 0 .4rem;color:var(--ts-text-subtle);border:1px solid var(--ts-border-strong);border-radius:999px;background:var(--ts-surface);font-size:.72rem!important;font-weight:800!important;cursor:pointer;transition:color var(--ts-transition),border-color var(--ts-transition),background var(--ts-transition)}
    .switch-track{position:relative;width:30px;height:18px;flex:0 0 30px;border-radius:999px;background:var(--ts-border-strong);transition:background var(--ts-transition)}
    .switch-knob{position:absolute;top:2px;left:2px;width:14px;height:14px;border-radius:50%;background:var(--ts-bg-gray-0);box-shadow:0 1px 3px var(--ts-shadow-gray-1000-a30);transition:transform 220ms cubic-bezier(.2,.8,.2,1)}
    .visibility-switch.on{color:var(--ts-success);border-color:color-mix(in srgb,var(--ts-success) 45%,transparent);background:color-mix(in srgb,var(--ts-success) 10%,transparent)}
    .visibility-switch.on .switch-track{background:var(--ts-success)}
    .visibility-switch.on .switch-knob{transform:translateX(12px)}
    .visibility-switch:focus-visible{outline:3px solid color-mix(in srgb,var(--ts-primary) 35%,transparent);outline-offset:2px}
    .icon-action{display:grid;place-items:center;width:34px;height:34px;min-height:34px!important;flex:0 0 34px;box-sizing:border-box;padding:0;border:1px solid var(--ts-border-strong);border-radius:10px;color:var(--ts-text-muted);background:color-mix(in srgb,var(--ts-primary) 8%,transparent);text-decoration:none;cursor:pointer;transition:background var(--ts-transition),color var(--ts-transition)}
    .icon-action:hover{background:color-mix(in srgb,var(--ts-primary) 18%,transparent);color:var(--ts-text)}
    .icon-action.is-disabled{opacity:.3;pointer-events:none}
    .icon-action.danger{color:var(--ts-danger);border-color:color-mix(in srgb,var(--ts-danger) 40%,transparent);background:color-mix(in srgb,var(--ts-danger) 10%,transparent)}
    .icon-action.danger:hover{background:color-mix(in srgb,var(--ts-danger) 22%,transparent);color:var(--ts-text)}

    .social-empty{display:grid;justify-items:center;gap:.35rem;padding:2rem 1rem;text-align:center;color:var(--ts-text-muted);border:1px dashed var(--ts-border-strong);border-radius:14px;font-size:.8rem}
    .empty-icon{display:grid;place-items:center;width:52px;height:52px;margin-bottom:.3rem;color:var(--ts-primary);border-radius:16px;background:color-mix(in srgb,var(--ts-primary) 14%,transparent);font-size:1.4rem}
    .social-empty strong{color:var(--ts-text);font-size:.95rem}
    .social-empty .primary-add{margin-top:.6rem}

    /* ---------- Barra de guardado (fija abajo mientras haya cambios) ---------- */
    /* Anclada al borde inferior (bottom:0) y con fondo opaco: sin hueco por
       donde se vea pasar el contenido al desplazarse. */
    .social-actions{position:sticky;bottom:0;z-index:6;display:flex;align-items:center;justify-content:space-between;gap:.8rem;margin:0 -1.2rem;padding:.85rem 1.2rem;border-top:1px solid var(--ts-border);border-radius:0 0 16px 16px;background:var(--ts-bg-elevated);transition:box-shadow var(--ts-transition),border-color var(--ts-transition)}
    .social-actions.dirty{border-top-color:color-mix(in srgb,var(--ts-warning) 55%,transparent);box-shadow:0 -12px 26px var(--ts-shadow-gray-1000-a32),inset 0 2px 0 color-mix(in srgb,var(--ts-warning) 35%,transparent);animation:bar-in .25s cubic-bezier(.2,.8,.2,1)}
    .save-state{display:inline-flex;align-items:center;gap:.45rem;color:var(--ts-text-subtle);font-size:.76rem;font-weight:700}
    .save-state i{color:var(--ts-success)}
    .save-state.pending{color:var(--ts-text)}
    .save-state.pending i{color:var(--ts-warning);font-size:.45rem;animation:pulse 1.6s ease-in-out infinite}
    .action-buttons{display:flex;gap:.6rem}
    .action-buttons .action-btn i{margin-right:.35rem}
    .spin{display:inline-block;animation:spin .8s linear infinite}

    /* ---------- Vista previa ---------- */
    .preview-card{position:sticky;top:16px;display:grid;gap:.9rem;padding:1rem}
    .preview-card>header{display:flex;align-items:center;justify-content:space-between}
    .preview-badge{padding:.2rem .55rem;color:var(--ts-success);border:1px solid color-mix(in srgb,var(--ts-success) 40%,transparent);border-radius:999px;font-size:.62rem;font-weight:800}
    .preview-badge.pending{color:var(--ts-warning);border-color:color-mix(in srgb,var(--ts-warning) 45%,transparent)}
    .preview-block{display:grid;gap:.45rem}
    .preview-block>small{display:flex;align-items:center;gap:.4rem;color:var(--ts-text-muted);font-size:.7rem;font-weight:700}
    .mock-footer{display:grid;gap:.55rem;padding:.85rem;border-radius:12px;background:linear-gradient(180deg,var(--ts-bg-navy-900-14),var(--ts-bg-navy-950-3));border:1px solid var(--ts-border-sky-450-a22)}
    .mock-footer-title{color:var(--ts-text-gray-0);font-size:.62rem;font-weight:800;letter-spacing:.1em;text-transform:uppercase}
    .mock-icons{display:flex;flex-wrap:wrap;gap:.4rem}
    .mock-icons.centered{justify-content:center}
    .mock-icon{display:grid;place-items:center;width:32px;height:32px;color:var(--ts-text-sky-150-28);border:1px solid var(--ts-border-blue-350-a26);border-radius:50%;background:var(--ts-bg-navy-850-a55-2);font-size:.85rem;text-decoration:none;animation:pop-in .26s cubic-bezier(.2,.8,.2,1) both;transition:transform var(--ts-transition),color var(--ts-transition),background var(--ts-transition),border-color var(--ts-transition)}
    .mock-icon:hover,.mock-icon.filled{color:var(--on-brand);background:var(--brand);border-color:var(--brand)}
    .mock-icon:hover{transform:translateY(-2px)}
    .mock-kubo-label{display:inline-flex;align-items:center;gap:.35rem}.mock-kubo-avatar{width:22px;height:22px}
    .mock-kubo{display:grid;gap:.6rem;padding:.7rem;border-radius:12px;background:var(--ts-surface);border:1px solid var(--ts-border)}
    .mock-kubo-option{display:flex;align-items:center;gap:.55rem;padding:.5rem .6rem;border:1px solid color-mix(in srgb,var(--ts-primary) 50%,transparent);border-radius:10px;background:color-mix(in srgb,var(--ts-primary) 10%,transparent)}
    .mock-kubo-option>span{display:grid;place-items:center;width:26px;height:26px;color:var(--ts-primary);border-radius:8px;background:color-mix(in srgb,var(--ts-primary) 16%,transparent);font-size:.75rem}
    .mock-kubo-option div{display:grid;flex:1;min-width:0}
    .mock-kubo-option strong{color:var(--ts-text);font-size:.72rem}
    .mock-kubo-option em{color:var(--ts-text-subtle);font-size:.62rem;font-style:normal}
    .mock-kubo-option>i{color:var(--ts-primary);font-size:.7rem}
    .preview-empty{display:flex;align-items:center;gap:.4rem;color:var(--ts-text-subtle);font-size:.72rem}
    .preview-tips{display:grid;gap:.5rem;margin:0;padding:.8rem 0 0;border-top:1px solid var(--ts-border);list-style:none}
    .preview-tips li{display:flex;gap:.5rem;color:var(--ts-text-muted);font-size:.72rem;line-height:1.45}
    .preview-tips i{color:var(--ts-primary)}
    .preview-tips strong{color:var(--ts-text)}

    /* ---------- Modal de creación ---------- */
    .social-modal{display:flex!important;flex-direction:column;width:min(600px,100%)!important;max-height:min(680px,calc(100dvh - 32px))!important;padding:0!important;overflow:hidden!important;color:var(--ts-text);border:1px solid var(--ts-border-strong);border-radius:18px!important;background:linear-gradient(165deg,var(--ts-surface),var(--ts-bg-elevated));box-shadow:var(--ts-shadow-floating);animation:modal-in .24s cubic-bezier(.2,.8,.2,1) both}
    .sm-header{display:flex;align-items:center;gap:.8rem;padding:1.05rem 1.15rem;border-bottom:1px solid var(--ts-border);background:linear-gradient(180deg,color-mix(in srgb,var(--brand,var(--ts-primary)) 12%,transparent),transparent)}
    .sm-badge{display:grid;place-items:center;width:42px;height:42px;flex:0 0 42px;color:var(--ts-primary);border-radius:12px;background:color-mix(in srgb,var(--ts-primary) 16%,transparent);font-size:1.15rem;transition:background .25s ease,color .25s ease}
    .sm-badge.branded{color:var(--on-brand);background:var(--brand);box-shadow:0 8px 18px color-mix(in srgb,var(--brand) 30%,transparent)}
    .sm-heading{flex:1;min-width:0}
    .sm-heading h3{margin:0;color:var(--ts-text);font-size:1.05rem;font-weight:800}
    .sm-heading p{margin:.15rem 0 0;color:var(--ts-text-muted);font-size:.74rem}
    .sm-icon-btn{display:grid;place-items:center;width:36px;height:36px;min-height:36px!important;flex:0 0 36px;padding:0;color:var(--ts-text-muted);border:1px solid var(--ts-border);border-radius:10px;background:transparent;cursor:pointer;transition:background var(--ts-transition),color var(--ts-transition)}
    .sm-icon-btn:hover{color:var(--ts-text);background:var(--ts-surface-hover)}
    .sm-body{flex:1 1 auto;display:grid;align-content:start;gap:1rem;min-height:0;padding:1.1rem 1.15rem 1.15rem;overflow-y:auto}
    .sm-form{gap:1rem}
    .sm-form .field>span{font-size:.66rem!important}
    .sm-form .field :is(input){height:42px;min-height:42px!important;font-size:.86rem!important}
    .sm-form app-social-network-select{--nsel-height:42px}
    .visibility-switch.wide{width:100%;min-height:58px!important;height:auto;justify-content:flex-start;gap:.8rem;padding:.65rem .85rem;border-radius:12px;text-align:left}
    .switch-text{display:grid;gap:1px}
    .switch-text strong{font-size:.8rem}
    .switch-text em{color:var(--ts-text-subtle);font-size:.7rem;font-style:normal;font-weight:600}
    .sm-footer{display:flex;justify-content:flex-end;gap:.6rem;margin:.1rem -1.15rem -1.15rem;padding:.9rem 1.15rem;border-top:1px solid var(--ts-border);background:color-mix(in srgb,var(--ts-bg) 35%,transparent)}
    .sm-footer .action-btn i{margin-right:.3rem}

    .social-loading,.social-load-error{display:flex;align-items:center;gap:.7rem;padding:2rem;color:var(--ts-text-muted);font-size:.9rem}
    .social-load-error{flex-wrap:wrap;color:var(--ts-danger)}
    .social-load-error .secondary-btn{margin-left:auto}
    .spinner{width:20px;height:20px;border:3px solid color-mix(in srgb,var(--ts-primary) 30%,transparent);border-top-color:var(--ts-primary);border-radius:50%;animation:spin .8s linear infinite}

    @keyframes spin{to{transform:rotate(360deg)}}
    @keyframes row-in{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
    @keyframes pop-in{from{opacity:0;transform:scale(.7)}to{opacity:1;transform:none}}
    @keyframes modal-in{from{opacity:0;transform:translateY(12px) scale(.98)}to{opacity:1;transform:none}}
    @keyframes bar-in{from{opacity:.4;transform:translateY(8px)}to{opacity:1;transform:none}}
    @keyframes pulse{0%,100%{opacity:1}50%{opacity:.35}}

    /* ---------- Vista previa del sitio de un negocio (modo business) ---------- */
    .mock-site{display:grid;gap:.45rem;padding:.9rem;border-radius:12px;background:var(--ts-bg-amber-50-3);border:1px solid var(--ts-border-navy-900-a8)}
    .mock-site-kicker{display:inline-flex;align-items:center;gap:.3rem;color:var(--ts-text-blue-450-7);font-size:.58rem;font-weight:800;letter-spacing:.08em;text-transform:uppercase}
    .mock-site-title{color:var(--ts-text-navy-850-5)!important;font-size:.95rem;font-weight:800}
    .mock-site-lines{display:grid;gap:.3rem;margin-bottom:.2rem}
    .mock-site-lines span{height:6px;border-radius:4px;background:var(--ts-bg-navy-900-a10)}
    .mock-site-lines span:last-child{width:60%}
    .mock-icon.site{color:var(--brand)!important;border-color:var(--ts-border-navy-900-a14);background:var(--ts-bg-amber-0-2);box-shadow:0 3px 8px var(--ts-shadow-gray-1000-a10)}
    /* Redes con color de marca blanco (TikTok, X, Threads...): sobre fondo claro el ícono va en negro. */
    .mock-icon.site[style*="--brand: #ffffff"],.mock-icon.site[style*="--brand:#ffffff"]{color:var(--on-brand)!important}
    .mock-icon.site:hover{color:var(--on-brand)!important;background:var(--brand);border-color:var(--brand)}
    .mock-phone{position:relative;display:grid;gap:.6rem;width:min(220px,100%);margin:0 auto;padding:1.4rem .8rem 1rem;border:6px solid var(--ts-border-navy-850-2);border-radius:26px;background:var(--ts-surface)}
    .mock-phone-notch{position:absolute;top:6px;left:50%;width:54px;height:8px;border-radius:6px;background:var(--ts-bg-navy-850-63);transform:translateX(-50%)}
    .mock-phone .preview-empty{justify-content:center;text-align:center}

    /* ---------- Responsive ---------- */
    /* Container queries sobre .social-shell: el ancho disponible cambia mucho
       entre el panel de la plataforma y la columna de formulario de "Mi sitio". */
    .social-shell{container-type:inline-size;container-name:social-shell}
    @container social-shell (max-width: 1000px){
      /* Sin espacio lateral: el listado va primero y la vista previa debajo. */
      .social-layout{grid-template-columns:minmax(0,1fr)}
      .preview-card{position:static;grid-template-columns:repeat(2,minmax(0,1fr));align-items:start}
      .preview-card>header,.preview-tips{grid-column:1/-1}
      .preview-tips{grid-template-columns:repeat(3,minmax(0,1fr))}
    }
    @container social-shell (max-width: 640px){
      .social-card{padding:.95rem .9rem 0}
      .social-actions{margin:0 -.9rem;padding:.75rem .9rem}
      .card-head .primary-add{width:100%}
      .preview-card{grid-template-columns:1fr}
      .preview-tips{grid-template-columns:1fr}
      .social-actions{flex-wrap:wrap}
      .action-buttons{width:100%}
      .action-buttons button{flex:1}
    }
    @container social-shell (max-width: 420px){
      .quick-add{padding:.55rem}
      .quick-btn span{display:none}
      .quick-btn{padding:0 .35rem}
    }
    @media(max-width:440px){
      .sm-footer{flex-direction:column-reverse}
      .sm-footer button{width:100%}
    }
    @media(prefers-reduced-motion:reduce){
      .social-row,.mock-icon,.social-modal,.save-state.pending i{animation:none}
      .switch-knob,.primary-add{transition:none}
    }
  `],
})
export class PlatformSocialLinksAdminComponent implements OnInit, OnChanges {
  /**
   * 'platform' (por defecto): redes oficiales de TuSala, carga y guarda por
   * su propio endpoint. 'business': redes de un negocio en "Mi sitio" -- el
   * componente es controlado: recibe el borrador y lo último guardado por
   * entradas, emite cada cambio (draftChange) y delega el guardado al
   * borrador del sitio (saveRequest), que luego se publica con "Publicar cambios".
   */
  @Input() mode: 'platform' | 'business' = 'platform';
  @Input() draftLinks: BusinessSocialLink[] | null = null;
  @Input() savedLinks: BusinessSocialLink[] | null = null;
  @Input() catalogOptions: SocialNetworkOption[] | null = null;
  @Input() suggestedOptions: string[] | null = null;
  @Input() limitValue: number | null = null;
  @Input() externalSaving = false;
  /** Error devuelto al guardar el borrador, para resaltar la fila exacta. */
  @Input() externalError: { index: number | null; message: string } | null = null;
  @Output() draftChange = new EventEmitter<BusinessSocialLink[]>();
  @Output() saveRequest = new EventEmitter<void>();

  get isBusiness(): boolean {
    return this.mode === 'business';
  }

  private readonly svc = inject(PlatformSocialLinksService);
  private readonly notifications = inject(NotificationService);
  private readonly dialogs = inject(DialogService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly loading = signal(true);
  readonly loadError = signal('');
  readonly saving = signal(false);
  readonly rows = signal<SocialRow[]>([]);
  readonly catalog = signal<SocialNetworkOption[]>([]);
  readonly suggested = signal<string[]>([]);
  readonly limit = signal(20);
  readonly errors = signal<Record<number, string>>({});
  readonly liveMessage = signal('');
  private readonly snapshot = signal('[]');
  private nextUid = 1;

  // Modal de creación
  readonly createOpen = signal(false);
  readonly draftError = signal('');
  readonly networkError = signal('');
  draft: DraftLink = { network: '', label: '', url: '', is_active: true };
  @ViewChild('draftSelect') private draftSelect?: SocialNetworkSelectComponent;

  // Arrastrar y soltar
  readonly dragUid = signal<number | null>(null);
  private dragGrabOffset = 0;
  private dragPointerY = 0;
  private autoScrollFrame: number | null = null;

  readonly catalogMap = computed(() => new Map(this.catalog().map(n => [n.key, n])));
  readonly usedNetworks = computed(() => new Set(this.rows().map(r => r.network)));
  readonly atLimit = computed(() => this.rows().length >= this.limit());
  readonly dirty = computed(() => this.serialize(this.rows()) !== this.snapshot());
  readonly missingSuggested = computed(() =>
    this.suggested().filter(key => !this.usedNetworks().has(key)).map(key => this.network(key)));
  readonly previewLinks = computed(() => this.rows()
    .filter(row => row.is_active)
    .map(row => ({ ...row, url: normalizeSocialUrl(row.network, row.url) || '' }))
    .filter(row => !!row.url));

  ngOnInit(): void {
    if (this.isBusiness) {
      this.loading.set(false);
      return;
    }
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isBusiness) return;
    if (changes['catalogOptions'] && this.catalogOptions) this.catalog.set(this.catalogOptions);
    if (changes['suggestedOptions'] && this.suggestedOptions) this.suggested.set(this.suggestedOptions);
    if (changes['limitValue'] && this.limitValue) this.limit.set(this.limitValue);
    if (changes['savedLinks']) this.snapshot.set(this.serialize(this.toRows(this.savedLinks || [])));
    // Solo se rehidrata si el borrador que llega es distinto de lo que ya hay
    // en pantalla (evita perder el uid de las filas con cada cambio propio).
    if (changes['draftLinks']) {
      const incoming = this.toRows(this.draftLinks || []);
      if (this.serialize(incoming) !== this.serialize(this.rows())) {
        this.rows.set(incoming);
        this.errors.set({});
      }
    }
    if (changes['externalError'] && this.externalError) {
      const row = this.externalError.index !== null ? this.rows()[this.externalError.index] : undefined;
      if (row) {
        this.errors.set({ [row.uid]: this.externalError.message });
        this.focusUrl(row.uid);
      }
    }
  }

  isSaving(): boolean {
    return this.isBusiness ? this.externalSaving : this.saving();
  }

  private toRows(links: BusinessSocialLink[]): SocialRow[] {
    return links.map(link => ({ uid: this.nextUid++, id: null, network: link.network, label: link.label || '', url: link.url || '', is_active: !!link.is_active }));
  }

  /** En modo business, avisa al borrador de "Mi sitio" de cada cambio en la lista. */
  private emitDraft(): void {
    if (!this.isBusiness) return;
    this.draftChange.emit(this.rows().map(({ network, label, url, is_active }) => ({ network, label, url, is_active })));
  }

  load(): void {
    this.loading.set(true);
    this.loadError.set('');
    this.svc.load().subscribe({
      next: res => { this.hydrate(res); this.loading.set(false); },
      error: err => this.loadError.set(friendlyError(err, 'No se pudieron cargar las redes sociales de la plataforma.')),
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.createOpen()) this.closeCreate();
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    // En "Mi sitio" el aviso de cambios sin guardar lo maneja la propia página.
    if (!this.isBusiness && this.dirty() && !this.saving()) event.preventDefault();
  }

  network(key: string): SocialNetworkOption {
    return this.catalogMap().get(key) || this.catalogMap().get('other') || FALLBACK_NETWORK;
  }

  trackRow(_: number, row: { uid: number }): number {
    return row.uid;
  }

  openableUrl(row: SocialRow): string | null {
    return normalizeSocialUrl(row.network, row.url) || null;
  }

  patch(index: number, changes: Partial<PlatformSocialLink>): void {
    const uid = this.rows()[index]?.uid;
    this.rows.update(list => list.map((row, i) => (i === index ? { ...row, ...changes } : row)));
    if (uid !== undefined) this.clearError(uid);
    this.emitDraft();
  }

  changeNetwork(index: number, key: string): void {
    const row = this.rows()[index];
    if (!row) return;
    const previousDefault = this.network(row.network).label;
    const label = !row.label.trim() || row.label.trim() === previousDefault ? this.network(key).label : row.label;
    this.patch(index, { network: key, label });
  }

  normalizeRow(index: number): void {
    const row = this.rows()[index];
    if (!row || !row.url.trim()) return;
    const normalized = normalizeSocialUrl(row.network, row.url);
    if (normalized && normalized !== row.url) {
      this.rows.update(list => list.map((r, i) => (i === index ? { ...r, url: normalized } : r)));
      this.emitDraft();
    }
  }

  async remove(index: number): Promise<void> {
    const row = this.rows()[index];
    if (!row) return;
    if (row.url.trim()) {
      const confirmed = await this.dialogs.confirm({
        title: `Quitar ${row.label || 'red social'}`,
        message: this.isBusiness
          ? 'Dejará de mostrarse en tu sitio cuando guardes y publiques los cambios. Si solo quieres ocultarla por un tiempo, usa el interruptor "Visible".'
          : 'Dejará de mostrarse en todos los frentes cuando guardes los cambios. Si solo quieres ocultarla por un tiempo, usa el interruptor "Visible".',
        confirmText: 'Quitar red',
        cancelText: 'Cancelar',
        danger: true,
      });
      if (!confirmed) return;
    }
    this.clearError(row.uid);
    this.animateReorder(() => this.rows.update(list => list.filter(r => r.uid !== row.uid)));
  }

  // ---------------------------------------------------------------------------
  // Modal de creación
  // ---------------------------------------------------------------------------

  /** Abre el formulario; con `networkKey` (accesos rápidos) llega con la red ya elegida. */
  openCreate(networkKey?: string): void {
    if (this.atLimit()) {
      this.notifications.error(`Puedes registrar como máximo ${this.limit()} redes sociales.`);
      return;
    }
    this.draft = { network: '', label: '', url: '', is_active: true };
    this.draftError.set('');
    this.networkError.set('');
    this.createOpen.set(true);
    if (networkKey) {
      this.pickNetwork(networkKey);
    } else {
      // Sin red elegida: se despliega el buscador de redes de inmediato.
      setTimeout(() => this.draftSelect?.openPanel(), 80);
    }
  }

  /** La red elegida en el formulario de creación (null mientras no hay ninguna). */
  draftNetwork(): SocialNetworkOption | null {
    return this.draft.network ? this.network(this.draft.network) : null;
  }

  pickNetwork(key: string): void {
    const net = this.network(key);
    const previous = this.draftNetwork();
    const keepLabel = this.draft.label.trim() && this.draft.label.trim() !== previous?.label;
    this.draft = { ...this.draft, network: net.key, label: keepLabel ? this.draft.label : net.label };
    this.draftError.set('');
    this.networkError.set('');
    this.focusLater('social-draft-url');
  }

  draftHint(): string {
    if (this.draft.network === 'whatsapp') return 'Puedes pegar el número con código de país (ej. +503 7000-0000) y se convierte en enlace.';
    if (this.draft.network === 'telegram') return 'Puedes escribir @usuario y se convierte en enlace.';
    return 'Pega la dirección del perfil; si falta https:// se agrega automáticamente.';
  }

  confirmCreate(): void {
    if (!this.draft.network) {
      this.networkError.set('Elige la red social que deseas agregar.');
      this.draftSelect?.openPanel();
      return;
    }
    const net = this.network(this.draft.network);
    const url = normalizeSocialUrl(net.key, this.draft.url);
    if (url === null) {
      this.draftError.set(`El enlace no es válido. Ejemplo: ${net.placeholder}`);
      this.focusLater('social-draft-url');
      return;
    }
    if (!url && this.draft.is_active) {
      this.draftError.set('Agrega el enlace o marca la red como oculta para completarlo después.');
      this.focusLater('social-draft-url');
      return;
    }
    const duplicate = url && this.rows().find(r => (normalizeSocialUrl(r.network, r.url) || '').toLowerCase().replace(/\/+$/, '') === url.toLowerCase().replace(/\/+$/, ''));
    if (duplicate) {
      this.draftError.set(`Este enlace ya está registrado en ${duplicate.label}.`);
      this.focusLater('social-draft-url');
      return;
    }
    const uid = this.nextUid++;
    const label = this.draft.label.trim().replace(/\s+/g, ' ') || net.label;
    this.rows.update(list => [...list, { uid, id: null, network: net.key, label, url, is_active: this.draft.is_active }]);
    this.emitDraft();
    this.liveMessage.set(`${label} agregada a la lista. Recuerda guardar los cambios.`);
    this.closeCreate();
    setTimeout(() => {
      const row = this.rowElement(uid);
      row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      row?.animate([{ boxShadow: '0 0 0 3px color-mix(in srgb, var(--ts-success) 55%, transparent)' }, { boxShadow: '0 0 0 0 transparent' }], { duration: 1400, easing: 'ease-out' });
    }, 60);
  }

  closeCreate(): void {
    this.createOpen.set(false);
    this.draft = { network: '', label: '', url: '', is_active: true };
  }

  // ---------------------------------------------------------------------------
  // Arrastrar y soltar (Pointer Events: mouse, táctil y lápiz) + teclado
  // ---------------------------------------------------------------------------

  startDrag(event: PointerEvent, uid: number): void {
    if (event.button !== 0 || this.rows().length < 2) return;
    const row = this.rowElement(uid);
    if (!row) return;
    event.preventDefault();
    const handle = event.currentTarget as HTMLElement;
    handle.setPointerCapture(event.pointerId);
    handle.focus({ preventScroll: true });
    this.dragGrabOffset = event.clientY - row.getBoundingClientRect().top;
    this.dragPointerY = event.clientY;
    this.dragUid.set(uid);

    const move = (e: PointerEvent) => { this.dragPointerY = e.clientY; this.onDragMove(); };
    const end = () => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', end);
      handle.removeEventListener('pointercancel', end);
      this.finishDrag();
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
    this.autoScroll();
  }

  handleKey(event: KeyboardEvent, index: number): void {
    const delta = event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0;
    if (!delta) return;
    event.preventDefault();
    const target = index + delta;
    if (target < 0 || target >= this.rows().length) return;
    const uid = this.rows()[index].uid;
    this.moveRow(index, target);
    this.announcePosition(uid);
    setTimeout(() => (this.rowElement(uid)?.querySelector('.drag-handle') as HTMLElement | null)?.focus());
  }

  private onDragMove(): void {
    const uid = this.dragUid();
    if (uid === null) return;
    const list = this.rows();
    const from = list.findIndex(r => r.uid === uid);
    if (from < 0) return;

    // Posición de destino: se cruza la mitad de la fila vecina.
    let target = from;
    const pointer = this.dragPointerY;
    for (let i = 0; i < list.length; i++) {
      if (i === from) continue;
      const rect = this.rowElement(list[i].uid)?.getBoundingClientRect();
      if (!rect) continue;
      const middle = rect.top + rect.height / 2;
      if (i < from && pointer < middle) { target = Math.min(target, i); }
      if (i > from && pointer > middle) { target = Math.max(target, i); }
    }
    if (target !== from) this.moveRow(from, target);
    this.followPointer(uid);
  }

  /** La fila arrastrada sigue al puntero (sin salir del listado). */
  private followPointer(uid: number): void {
    const row = this.rowElement(uid);
    if (!row) return;
    row.style.transform = '';
    const slotTop = row.getBoundingClientRect().top;
    const listRect = row.parentElement?.getBoundingClientRect();
    let offset = this.dragPointerY - this.dragGrabOffset - slotTop;
    if (listRect) {
      const minOffset = listRect.top - slotTop - 6;
      const maxOffset = listRect.bottom - (slotTop + row.offsetHeight) + 6;
      offset = Math.max(minOffset, Math.min(maxOffset, offset));
    }
    row.style.transform = `translateY(${offset}px) scale(1.01)`;
  }

  private finishDrag(): void {
    const uid = this.dragUid();
    if (this.autoScrollFrame !== null) cancelAnimationFrame(this.autoScrollFrame);
    this.autoScrollFrame = null;
    if (uid === null) return;
    const row = this.rowElement(uid);
    if (row) {
      const current = row.style.transform;
      row.style.transform = '';
      if (current) row.animate([{ transform: current }, { transform: 'none' }], { duration: 180, easing: 'cubic-bezier(.2,.8,.2,1)' });
    }
    this.dragUid.set(null);
    this.announcePosition(uid);
  }

  /** Desplaza la página si se arrastra cerca del borde superior/inferior. */
  private autoScroll(): void {
    if (this.dragUid() === null) return;
    const edge = 70;
    const y = this.dragPointerY;
    const speed = y < edge ? -Math.ceil((edge - y) / 6) : y > window.innerHeight - edge ? Math.ceil((y - (window.innerHeight - edge)) / 6) : 0;
    if (speed) {
      window.scrollBy(0, speed);
      this.onDragMove();
    }
    this.autoScrollFrame = requestAnimationFrame(() => this.autoScroll());
  }

  private moveRow(from: number, to: number): void {
    this.animateReorder(() => this.rows.update(list => {
      const copy = [...list];
      const [item] = copy.splice(from, 1);
      copy.splice(to, 0, item);
      return copy;
    }));
  }

  /** Animación FLIP: cada fila se desliza desde su posición anterior. */
  private animateReorder(mutate: () => void): void {
    const dragging = this.dragUid();
    const before = new Map<number, number>();
    this.rowElements().forEach(el => before.set(Number(el.dataset['uid']), el.getBoundingClientRect().top));
    mutate();
    this.emitDraft();
    this.cdr.detectChanges();
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;
    this.rowElements().forEach(el => {
      const uid = Number(el.dataset['uid']);
      if (uid === dragging) return;
      const previous = before.get(uid);
      if (previous === undefined) return;
      const delta = previous - el.getBoundingClientRect().top;
      if (Math.abs(delta) < 1) return;
      el.animate([{ transform: `translateY(${delta}px)` }, { transform: 'none' }], { duration: 220, easing: 'cubic-bezier(.2,.8,.2,1)' });
    });
  }

  private announcePosition(uid: number): void {
    const index = this.rows().findIndex(r => r.uid === uid);
    const row = this.rows()[index];
    if (row) this.liveMessage.set(`${row.label || 'Red'} en la posición ${index + 1} de ${this.rows().length}.`);
  }

  private rowElements(): HTMLElement[] {
    return Array.from((this.host.nativeElement as HTMLElement).querySelectorAll<HTMLElement>('.social-row'));
  }

  private rowElement(uid: number): HTMLElement | null {
    return (this.host.nativeElement as HTMLElement).querySelector<HTMLElement>(`.social-row[data-uid="${uid}"]`);
  }

  // ---------------------------------------------------------------------------
  // Guardado
  // ---------------------------------------------------------------------------

  discard(): void {
    try {
      const saved = JSON.parse(this.snapshot()) as PlatformSocialLink[];
      this.rows.set(saved.map(link => ({ ...link, uid: this.nextUid++ })));
    } catch {
      this.rows.set([]);
    }
    this.errors.set({});
    this.emitDraft();
  }

  async save(): Promise<void> {
    if (this.isSaving()) return;
    const validation = this.validate();
    if (!validation.ok) {
      this.errors.set(validation.errors);
      const first = this.rows().find(row => validation.errors[row.uid]);
      if (first) {
        this.notifications.error(validation.errors[first.uid]);
        this.focusUrl(first.uid);
      }
      return;
    }
    this.errors.set({});
    const payload = validation.links;
    this.rows.update(list => list.map((row, i) => ({ ...row, url: payload[i].url, label: payload[i].label })));
    if (this.isBusiness) {
      this.emitDraft();
      this.saveRequest.emit();
      return;
    }

    const visible = payload.filter(link => link.is_active).length;
    this.saving.set(true);
    try {
      const res = await this.dialogs.confirmAndRun(
        {
          title: 'Guardar redes sociales',
          message: visible
            ? `Se publicarán ${visible} red(es) social(es) en el sitio de clientes, Kubo y la landing de TuSala. Los cambios pueden tardar hasta un minuto en verse en todos los frentes.`
            : 'No quedará ninguna red visible: la sección de redes sociales se ocultará en todos los frentes. ¿Deseas continuar?',
          tone: 'info',
          confirmText: 'Guardar redes',
        },
        () => this.svc.save(payload),
        'Redes sociales actualizadas correctamente.',
        err => friendlyError(err, 'No se pudieron guardar las redes sociales.'),
      );
      if (res) this.hydrate(res);
    } catch (err: any) {
      // El modal ya mostró el error; además se marca la fila exacta que el backend rechazó.
      const index = Number(err?.error?.index);
      const row = Number.isInteger(index) ? this.rows()[index] : undefined;
      if (row) {
        this.errors.set({ [row.uid]: friendlyError(err, 'Revisa el enlace de esta red.') });
        this.focusUrl(row.uid);
      }
    } finally {
      this.saving.set(false);
    }
  }

  private validate(): { ok: boolean; errors: Record<number, string>; links: PlatformSocialLink[] } {
    const rows = this.rows();
    const result = validateSocialLinks(rows, key => this.network(key));
    const errors: Record<number, string> = {};
    for (const [index, message] of Object.entries(result.errors)) errors[rows[Number(index)].uid] = message;
    return { ok: result.ok, errors, links: result.links };
  }

  private hydrate(res: PlatformSocialLinksResponse): void {
    this.catalog.set(res.catalog || []);
    this.suggested.set(res.suggested || []);
    this.limit.set(Number(res.limit) || 20);
    const rows = (res.links || []).map(link => ({
      uid: this.nextUid++,
      id: link.id,
      network: link.network,
      label: link.label,
      url: link.url || '',
      is_active: !!link.is_active,
    }));
    this.rows.set(rows);
    this.snapshot.set(this.serialize(rows));
    this.errors.set({});
  }

  private serialize(rows: SocialRow[]): string {
    return JSON.stringify(rows.map(({ id, network, label, url, is_active }) => ({ id, network, label, url, is_active })));
  }

  private clearError(uid: number): void {
    if (!this.errors()[uid]) return;
    this.errors.update(map => {
      const copy = { ...map };
      delete copy[uid];
      return copy;
    });
  }

  private focusLater(id: string): void {
    setTimeout(() => (document.getElementById(id) as HTMLInputElement | null)?.focus(), 80);
  }

  private focusUrl(uid: number): void {
    setTimeout(() => {
      const input = document.getElementById(`social-url-${uid}`) as HTMLInputElement | null;
      if (!input) return;
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      input.focus({ preventScroll: true });
    }, 60);
  }
}
